import React from 'react';
import { Link } from 'react-router-dom';
import { placeholder, micahPortraitMain } from '../data/products';
import SEO from '../components/seo/SEO';
import './AboutPage.css';

const pillars = [
  {
    icon: '◇',
    heading: 'Inspired by life',
    body: 'Moments, places, and emotions become the spark.',
  },
  {
    icon: '◇',
    heading: 'Created by hand',
    body: 'Each piece is thoughtfully made in small batches.',
  },
  {
    icon: '◇',
    heading: 'Made to connect',
    body: 'Art that tells a story. Scents that hold a memory. Music that moves you.',
  },
];

const studioImages = [
  { src: placeholder(400, 320, 'Painting', '#D8CFC4'), alt: 'Original painting in progress' },
  { src: placeholder(400, 320, 'Fragrance', '#EFE8DD'), alt: 'Mixing natural fragrance oils' },
  { src: placeholder(400, 320, 'Music', '#C8C2B5'), alt: 'Micah playing guitar in the studio' },
  { src: placeholder(400, 320, 'Open Studio', '#D4CEC7'), alt: 'Open studio event' },
];

const personJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Micah Lynise',
  alternateName: 'Micah Dadson',
  url: 'https://micahlynise.com',
  sameAs: [],
  jobTitle: 'Artist',
  description: 'Multidisciplinary artist creating fine art, natural fragrances, body care, and music. Based in Austin, Texas.',
  knowsAbout: ['Fine Art', 'Painting', 'Natural Fragrance', 'Music Production', 'Skincare'],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Austin',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
});

const AboutPage: React.FC = () => (
  <div className="about-page">
    <SEO
      title="About"
      description="Micah Lynise is a multidisciplinary artist based in Austin, Texas — creating fine art, natural fragrances, body care, and original music."
      path="/about"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: personJsonLd }} />
    {/* Intro section */}
    <section className="about-intro">
      <div className="about-intro__layout container">
        <div className="about-intro__copy">
          <h1 className="about-intro__title">About Micah Lynise</h1>
          <p className="about-intro__body">
            I am an Austin-based painter, musician, and maker. Much of what I create has grown from a desire to build something meaningful from the grief that has shaped my life, particularly through my experiences with my mother and twin. Creating became a way for me to keep experimenting, growing, and remaining open to the world rather than shrinking inward.
          </p>
          <p className="about-intro__body">
            I began as an acrylic and oil painter, and over time I became interested in how art could move beyond the wall and become part of everyday life. That curiosity led me to bring my paintings into handmade body care and fragrance, adapting my original artwork into labels for body butters, body oils, and oil fragrances that I formulate and make myself.
             I write and perform original music, creating from many of the same emotions, memories, and observations that find their way into my paintings and fragrances.
          </p>
          <p className="about-intro__body">
            For me, creating is a way of building beyond grief. It is a way to continue exploring, experimenting, and making a life that feels expansive, and to invite other people into the worlds I create along the way.
          </p>
        </div>

        <div className="about-intro__image-col">
          <img
            src={micahPortraitMain}
            alt="Micah Lynise, artist"
            className="about-intro__image"
            width={560}
            height={500}
          />
        </div>
      </div>
    </section>

    {/* Process pillars */}
    <section className="about-pillars" aria-label="Our process">
      <div className="container">
        <div className="about-pillars__grid">
          {pillars.map((p) => (
            <div key={p.heading} className="about-pillar">
              <span className="about-pillar__icon" aria-hidden="true">{p.icon}</span>
              <h3 className="about-pillar__heading">{p.heading}</h3>
              <p className="about-pillar__body">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Artist statement */}
    <section className="about-statement">
      <div className="about-statement__inner container">
        <p className="about-statement__eyebrow">Artist Statement</p>
        <blockquote className="about-statement__quote">
          "Painting, music, and scent all give me different ways to create a world of my own and allow me to feel strongly. The scent I wear can shape how I move througout the day, whether I want to feel luxurious, soft, grounded, or strong. Pairing each scent with artwork allows the visual world of the painting to reinforce that feeling, giving each product its own atmosphere and identity."
        </blockquote>
        <p className="about-statement__attribution">— Micah Lynise</p>
      </div>
    </section>

    {/* Studio gallery */}
    <section className="about-gallery" aria-label="Studio gallery">
      <div className="container">
        <p className="about-gallery__eyebrow">In the Studio</p>
        <div className="about-gallery__grid">
          {studioImages.map((img) => (
            <div key={img.alt} className="about-gallery__item">
              <img
                src={img.src}
                alt={img.alt}
                className="about-gallery__image"
                width={400}
                height={320}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default AboutPage;
